import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { usePreferences } from './PreferencesContext';
import { DEFAULT_LANGUAGE, getActiveLanguage, getInitialLanguageSource, setActiveLanguage, makeTranslator, intlLocale } from '../i18n';
import { LANGUAGES, LIVE_LANGUAGES } from '../i18n/languages';

const SUPPORTED_LANGUAGES = LIVE_LANGUAGES.map(l => l.code);

// Names are shown in their own language so anyone can find theirs.
const LANGUAGE_NAMES = Object.fromEntries(LANGUAGES.map(l => [l.code, l.name]));

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const { preferences, updatePreference, loaded } = usePreferences();
    const [language, setLanguageState] = useState(getActiveLanguage);
    const serverSynced = useRef(false);

    // Sync with server preferences once loaded; a language opened by address (/hr/…) wins and is saved.
    useEffect(() => {
        if (!loaded || serverSynced.current) return;
        serverSynced.current = true;
        if (getInitialLanguageSource() === 'url') {
            if (preferences.language !== language) updatePreference('language', language);
        } else if (preferences.language && SUPPORTED_LANGUAGES.includes(preferences.language)) {
            setLanguageState(preferences.language);
        }
    }, [loaded, preferences.language, language, updatePreference]);

    // Module-level t() (services, error fallbacks) and <html lang> follow the UI language.
    setActiveLanguage(language);
    useEffect(() => { setActiveLanguage(language); }, [language]);

    const setLanguage = useCallback((newLang) => {
        if (SUPPORTED_LANGUAGES.includes(newLang)) {
            setLanguageState(newLang);
            localStorage.setItem('language', newLang);
            updatePreference('language', newLang);
        }
    }, [updatePreference]);

    const value = useMemo(() => ({
        language,
        setLanguage,
        t: makeTranslator(language),
        locale: intlLocale(language),
        SUPPORTED_LANGUAGES,
        LANGUAGE_NAMES
    }), [language, setLanguage]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }
    return context;
}

// { t, language, locale } — t(key, vars) plus t.rich(key, components, vars) for inline markup.
export function useTranslation() {
    const { t, language, locale } = useLanguage();
    return { t, language, locale };
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_NAMES };
